import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  VStack,
  Text,
  Alert,
  AlertIcon,
  useToast,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { Navigate, useNavigate } from "react-router-dom";
import { InputPassword } from "../../components/Input";
import { useSSO } from "../../context/SSO";
import { resetPassword } from "../../services/authService";

const resetPasswordSchema = yup.object({
  newPassword: yup
    .string()
    .required("Nova senha é obrigatória")
    .min(6, "A senha deve ter no mínimo 6 caracteres")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "A senha deve conter pelo menos uma letra maiúscula, uma minúscula e um número"
    ),
  confirmPassword: yup
    .string()
    .required("Confirmação de senha é obrigatória")
    .oneOf([yup.ref("newPassword")], "As senhas não coincidem"),
});

export default function ResetPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const toast = useToast();
  const navigate = useNavigate();
  const { authenticated, isAuthLoading, logout, user } = useSSO();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Se ainda está carregando a autenticação, mostra spinner
  if (isAuthLoading) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Verificando autenticação...</Text>
        </VStack>
      </Center>
    );
  }

  // Se não está autenticado, redireciona para login
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError("");

    try {
      await resetPassword(data.newPassword);
      
      toast({
        title: "Senha redefinida com sucesso",
        description: "Você será redirecionado para a página inicial",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Aguarda 2 segundos e redireciona
      setTimeout(() => {
        // Obtém roles do contexto SSO
        const userRoles = user?.user?.roles || [];
        const hasMasterRole = userRoles.some(role => role.name === 'MASTER');
        const hasAdminCompanyRole = userRoles.some(role => role.name === 'RESPONSAVEL');
        
        if (hasMasterRole) {
          navigate("/registration", { replace: true });
        } else if (hasAdminCompanyRole) {
          navigate("/survey-analysis", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }, 2000);
    } catch (err) {
      const errorMessage = err.message || "Erro ao redefinir senha";
      setError(errorMessage);
      toast({
        title: "Erro",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      px={4}
    >
      <Container maxW="md" py={12}>
        <VStack spacing={8} align="stretch">
          <VStack spacing={2} textAlign="center">
            <Heading
              as="h1"
              size="xl"
              fontWeight="bold"
              color="gray.800"
            >
              Primeiro Acesso
            </Heading>
            <Text color="gray.600" fontSize="md">
              Por favor, defina uma nova senha para continuar
            </Text>
          </VStack>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Box
            bg="white"
            p={8}
            borderRadius="lg"
            boxShadow="md"
            as="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <VStack spacing={6}>
              <InputPassword
                label="Nova Senha"
                placeholder="Digite sua nova senha"
                error={errors.newPassword}
                {...register("newPassword")}
              />

              <InputPassword
                label="Confirmar Senha"
                placeholder="Digite novamente sua senha"
                error={errors.confirmPassword}
                {...register("confirmPassword")}
              />

              <Text fontSize="sm" color="gray.600" textAlign="left" w="100%">
                A senha deve conter:
                <br />• No mínimo 6 caracteres
                <br />• Pelo menos uma letra maiúscula
                <br />• Pelo menos uma letra minúscula
                <br />• Pelo menos um número
              </Text>

              <VStack spacing={3} w="100%">
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  fontSize="md"
                  w="100%"
                  isLoading={isLoading}
                  loadingText="Redefinindo..."
                >
                  Redefinir Senha
                </Button>

                <Button
                  variant="ghost"
                  size="lg"
                  fontSize="md"
                  w="100%"
                  onClick={handleCancel}
                  isDisabled={isLoading}
                >
                  Cancelar
                </Button>
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Flex>
  );
}
